import { useCallback, useEffect, useMemo, useState } from 'react';

import { api, mutate } from '../shared/api';

type Locale = 'hr' | 'en';

type Company = { id: number; code: string; name: string };
type Branch = { id: number; code: string | null; name: string; company_id: number; region: string | null };
type StateResp = {
    role: string; is_admin: boolean; is_voditelj: boolean; is_manager: boolean; is_posebno: boolean;
    companies: Company[]; branches: Branch[]; my_regions: string[]; expiry_minutes: number;
};
type PriceEntry = {
    id: number; effective_date: string; company_id: number;
    polazna_cijena_eur_g: string | number; maksimalna_cijena_eur_g: string | number; nadmasivanje_eur: string | number;
    created_by_name?: string | null; created_at: string;
};
type RequestRow = {
    id: number; company_id: number; company_code?: string | null; branch_dept_id: number; branch_name?: string | null;
    seller_user_id: number; seller_name?: string | null; weight_g: string | number; fineness_permille: number | null;
    competitor_name: string | null; competitor_price_eur_g: string | number; evidence_reference: string | null;
    polazna_cijena_eur_g: string | number; maksimalna_cijena_eur_g: string | number; requested_price_eur_g: string | number;
    approval_stage: 'voditelj' | 'posebno'; status: 'pending' | 'approved' | 'rejected' | 'expired';
    expires_at: string; created_at: string;
    approval_decision?: string | null; approval_notes?: string | null; approver_name?: string | null;
};
type EventRow = { id: number; event: string; from_status: string | null; to_status: string | null; note: string | null; user_name?: string | null; created_at: string };
type SettingsUser = { id: number; name: string; email: string; role: string };
type VoditeljRegion = { voditelj_user_id: number; region: string };
type SettingsResp = { users: SettingsUser[]; roles: Record<string, string>; voditelj_regions: VoditeljRegion[]; regions: string[] };

const card: React.CSSProperties = { border: '1px solid #e3d9bf', borderRadius: 10, padding: 16, marginBottom: 16, background: '#fffdf6' };
const input: React.CSSProperties = { background: '#fff', color: '#2f3337', border: '1px solid #d8cdb2', borderRadius: 8, padding: '7px 10px', fontSize: 13 };
const btn: React.CSSProperties = { background: '#8a5a1f', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 };
const btnGhost: React.CSSProperties = { background: '#fffdf6', color: '#2f3337', border: '1px solid #d8cdb2', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 13 };
const lbl: React.CSSProperties = { fontSize: 11, opacity: 0.6, display: 'block', marginBottom: 2 };
const TH: React.CSSProperties = { background: '#f4ead2', textAlign: 'left', padding: 6, fontSize: 12 };

const STATUS_META: Record<string, { hr: string; en: string; color: string }> = {
    pending: { hr: 'Na čekanju', en: 'Pending', color: '#a07a2f' },
    approved: { hr: 'Odobreno', en: 'Approved', color: '#1f7d4d' },
    rejected: { hr: 'Odbijeno', en: 'Rejected', color: '#b3261e' },
    expired: { hr: 'Isteklo', en: 'Expired', color: '#888' },
};

const fmtEur = (n: string | number | null | undefined) => (n == null ? '—' : Number(n).toLocaleString('hr-HR', { minimumFractionDigits: 4, maximumFractionDigits: 4 }) + ' €/g');

type TabKey = 'zahtjev' | 'cijena' | 'odobravanje' | 'dnevnik' | 'postavke';

export function Otkup50Module({ token, locale, onBack, embedded }: { token: string | null; locale: Locale; onBack: () => void; embedded?: boolean }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [state, setState] = useState<StateResp | null>(null);
    const [tab, setTab] = useState<TabKey>('zahtjev');

    const reloadState = useCallback(async () => { setState(await api<StateResp>('/otkup50/state', token)); }, [token]);
    useEffect(() => { void reloadState(); }, [reloadState]);

    const canApprove = !!state && (state.is_admin || state.is_voditelj || state.is_posebno);
    const canManagePrice = !!state && (state.is_admin || state.is_manager);
    const canAdmin = !!state && state.is_admin;

    const tabBtn = (k: TabKey, label: string) => (
        <button type="button" onClick={() => setTab(k)} style={{ padding: '8px 16px', border: 'none', borderBottom: tab === k ? '3px solid #8a5a1f' : '3px solid transparent', background: 'transparent', color: tab === k ? '#5c3b14' : '#888', fontWeight: 700, cursor: 'pointer' }}>{label}</button>
    );

    return (
        <div style={{ padding: '18px 20px', color: '#2f3337', maxWidth: 1100 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                {!embedded ? <button onClick={onBack} style={btnGhost}>← {t('Natrag', 'Back')}</button> : null}
                <h2 style={{ margin: 0, fontSize: 20 }}>🪙 {t('Otkup 50g+', 'Buyback 50g+')} <span style={{ color: '#bbb', fontSize: '0.6em' }}>m.26</span></h2>
            </div>
            <p style={{ color: '#888', fontSize: 12, marginTop: 0, marginBottom: 14 }}>
                {t('Test akcije povećane otkupne cijene zlatnog loma 50g+ (19.10.-18.12.2026).', 'Increased buyback price test for 50g+ scrap gold (19 Oct - 18 Dec 2026).')}
                {state ? <> · {t('moja uloga', 'my role')}: <b>{state.role}</b></> : null}
            </p>

            <div style={{ borderBottom: '1px solid #e3d9bf', marginBottom: 16 }}>
                {tabBtn('zahtjev', t('Zahtjev', 'Request'))}
                {canManagePrice ? tabBtn('cijena', t('Cijena dana', 'Daily price')) : null}
                {canApprove ? tabBtn('odobravanje', t('Na odobrenje', 'Pending approval')) : null}
                {canManagePrice ? tabBtn('dnevnik', t('Dnevnik', 'Audit log')) : null}
                {canAdmin ? tabBtn('postavke', t('Postavke', 'Settings')) : null}
            </div>

            {!state ? <div style={{ color: '#888' }}>…</div> : (
                <>
                    {tab === 'zahtjev' ? <ZahtjevTab token={token} locale={locale} state={state} /> : null}
                    {tab === 'cijena' && canManagePrice ? <CijenaTab token={token} locale={locale} state={state} /> : null}
                    {tab === 'odobravanje' && canApprove ? <OdobravanjeTab token={token} locale={locale} state={state} /> : null}
                    {tab === 'dnevnik' && canManagePrice ? <DnevnikTab token={token} locale={locale} /> : null}
                    {tab === 'postavke' && canAdmin ? <PostavkeTab token={token} locale={locale} /> : null}
                </>
            )}
        </div>
    );
}

// ── ZAHTJEV (prodavac) ──────────────────────────────────────────────────────
function ZahtjevTab({ token, locale, state }: { token: string | null; locale: Locale; state: StateResp }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [branchId, setBranchId] = useState('');
    const [weight, setWeight] = useState('');
    const [fineness, setFineness] = useState('750');
    const [competitorName, setCompetitorName] = useState('');
    const [competitorPrice, setCompetitorPrice] = useState('');
    const [evidence, setEvidence] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [rows, setRows] = useState<RequestRow[]>([]);

    const reload = useCallback(async () => {
        const d = await api<{ rows: RequestRow[] }>('/otkup50/requests?view=mine', token);
        setRows(d.rows);
    }, [token]);
    useEffect(() => { void reload(); }, [reload]);

    const submit = async () => {
        setError('');
        if (!weight || !competitorPrice) { setError(t('Težina i konkurentska cijena su obavezni.', 'Weight and competitor price are required.')); return; }
        setBusy(true);
        try {
            await mutate('/otkup50/requests', token, 'POST', {
                branch_dept_id: branchId ? Number(branchId) : undefined,
                weight_g: Number(weight),
                fineness_permille: fineness ? Number(fineness) : null,
                competitor_name: competitorName || null,
                competitor_price_eur_g: Number(competitorPrice),
                evidence_reference: evidence || null,
            });
            setWeight(''); setCompetitorName(''); setCompetitorPrice(''); setEvidence('');
            await reload();
        } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
    };

    return (
        <div>
            <div style={card}>
                <strong>{t('Novi zahtjev — konkurentska ponuda iznad polazne cijene', 'New request — competitor offer above baseline price')}</strong>
                <p style={{ color: '#888', fontSize: 12 }}>{t('Dokaz (foto konkurentske ponude) šalje se WhatsAppom izvan sustava — ovdje samo napomena da je dokaz poslan/primljen.', 'Evidence (competitor offer photo) is sent via WhatsApp outside the system — here just a note that it was sent/received.')}</p>
                {error ? <div style={{ background: '#fdecec', color: '#b3261e', padding: 8, borderRadius: 8, marginBottom: 8 }}>{error}</div> : null}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div>
                        <label style={lbl}>{t('Poslovnica', 'Branch')}</label>
                        <select value={branchId} onChange={(e) => setBranchId(e.target.value)} style={input}>
                            <option value="">{t('(moja poslovnica)', '(my branch)')}</option>
                            {state.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                    </div>
                    <div><label style={lbl}>{t('Bruto težina (g)', 'Gross weight (g)')}</label><input type="number" min={50} value={weight} onChange={(e) => setWeight(e.target.value)} style={{ ...input, width: 110 }} /></div>
                    <div><label style={lbl}>{t('Finoća (‰)', 'Fineness (‰)')}</label><input type="number" value={fineness} onChange={(e) => setFineness(e.target.value)} style={{ ...input, width: 90 }} /></div>
                    <div><label style={lbl}>{t('Konkurent (naziv)', 'Competitor (name)')}</label><input value={competitorName} onChange={(e) => setCompetitorName(e.target.value)} style={{ ...input, width: 160 }} /></div>
                    <div><label style={lbl}>{t('Konkurentska cijena (€/g fine)', 'Competitor price (€/g fine)')}</label><input type="number" step="0.0001" value={competitorPrice} onChange={(e) => setCompetitorPrice(e.target.value)} style={{ ...input, width: 140 }} /></div>
                    <div style={{ flex: 1, minWidth: 180 }}><label style={lbl}>{t('Dokaz (napomena)', 'Evidence (note)')}</label><input value={evidence} onChange={(e) => setEvidence(e.target.value)} placeholder={t('npr. dokaz primljen WhatsApp, potvrdio Ivan', 'e.g. evidence received via WhatsApp, confirmed by Ivan')} style={{ ...input, width: '100%' }} /></div>
                    <button disabled={busy} onClick={() => void submit()} style={btn}>{busy ? '…' : t('Pošalji zahtjev', 'Submit request')}</button>
                </div>
            </div>

            <strong>{t('Moji zahtjevi', 'My requests')}</strong>
            <RequestsTable rows={rows} locale={locale} showBranch showSeller={false} />
        </div>
    );
}

// ── CIJENA DANA (kategorijski manager) ───────────────────────────────────────
function CijenaTab({ token, locale, state }: { token: string | null; locale: Locale; state: StateResp }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [companyId, setCompanyId] = useState<string>(state.companies[0] ? String(state.companies[0].id) : '');
    const [entry, setEntry] = useState<PriceEntry | null>(null);
    const [suggestion, setSuggestion] = useState<number | null>(null);
    const [history, setHistory] = useState<PriceEntry[]>([]);
    const [polazna, setPolazna] = useState('');
    const [maksimalna, setMaksimalna] = useState('');
    const [nadmasivanje, setNadmasivanje] = useState('1');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const reload = useCallback(async () => {
        if (!companyId) return;
        const d = await api<{ entry: PriceEntry | null; suggested_polazna: number | null }>(`/otkup50/price?company_id=${companyId}`, token);
        setEntry(d.entry); setSuggestion(d.suggested_polazna);
        if (d.entry) { setPolazna(String(d.entry.polazna_cijena_eur_g)); setMaksimalna(String(d.entry.maksimalna_cijena_eur_g)); setNadmasivanje(String(d.entry.nadmasivanje_eur)); }
        const h = await api<{ rows: PriceEntry[] }>(`/otkup50/price/history?company_id=${companyId}`, token);
        setHistory(h.rows);
    }, [token, companyId]);
    useEffect(() => { void reload(); }, [reload]);

    const save = async () => {
        setError('');
        if (!polazna || !maksimalna) { setError(t('Polazna i maksimalna cijena su obavezne.', 'Baseline and maximum price are required.')); return; }
        setBusy(true);
        try {
            await mutate('/otkup50/price', token, 'POST', {
                company_id: Number(companyId), polazna_cijena_eur_g: Number(polazna),
                maksimalna_cijena_eur_g: Number(maksimalna), nadmasivanje_eur: Number(nadmasivanje || 1),
            });
            await reload();
        } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
    };

    return (
        <div>
            <div style={card}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div>
                        <label style={lbl}>{t('Tvrtka', 'Company')}</label>
                        <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} style={input}>
                            {state.companies.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
                        </select>
                    </div>
                    <div><label style={lbl}>{t('Polazna cijena (€/g fine)', 'Baseline price (€/g fine)')}</label><input type="number" step="0.0001" value={polazna} onChange={(e) => setPolazna(e.target.value)} style={{ ...input, width: 140 }} /></div>
                    <div><label style={lbl}>{t('Maksimalna otkupna cijena (€/g fine)', 'Maximum buyback price (€/g fine)')}</label><input type="number" step="0.0001" value={maksimalna} onChange={(e) => setMaksimalna(e.target.value)} style={{ ...input, width: 140 }} /></div>
                    <div><label style={lbl}>{t('Nadmašivanje (€)', 'Beat-by (€)')}</label><input type="number" step="0.0001" value={nadmasivanje} onChange={(e) => setNadmasivanje(e.target.value)} style={{ ...input, width: 100 }} /></div>
                    <button disabled={busy} onClick={() => void save()} style={btn}>{busy ? '…' : t('Spremi za danas', 'Save for today')}</button>
                </div>
                {error ? <div style={{ background: '#fdecec', color: '#b3261e', padding: 8, borderRadius: 8, marginTop: 8 }}>{error}</div> : null}
                <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
                    {suggestion != null ? <>{t('Predložak (spot - 10 €)', 'Suggestion (spot - €10)')}: <b>{fmtEur(suggestion)}</b> · </> : null}
                    {entry ? <>{t('Trenutno za danas', 'Current for today')}: {fmtEur(entry.polazna_cijena_eur_g)} / {fmtEur(entry.maksimalna_cijena_eur_g)} ({entry.created_by_name ?? '—'})</> : t('Jos nema unosa za danas.', 'No entry for today yet.')}
                </div>
            </div>

            <strong>{t('Povijest (po danu)', 'History (by day)')}</strong>
            <table style={{ width: '100%', marginTop: 6, fontSize: 13, borderCollapse: 'collapse' }}>
                <thead><tr><th style={TH}>{t('Datum', 'Date')}</th><th style={TH}>{t('Polazna', 'Baseline')}</th><th style={TH}>{t('Maksimalna', 'Maximum')}</th><th style={TH}>{t('Nadmašivanje', 'Beat-by')}</th><th style={TH}>{t('Unio', 'By')}</th></tr></thead>
                <tbody>
                    {history.map((h) => <tr key={h.id} style={{ borderBottom: '1px solid #f0e9d8' }}>
                        <td style={{ padding: 6 }}>{h.effective_date}</td><td>{fmtEur(h.polazna_cijena_eur_g)}</td><td>{fmtEur(h.maksimalna_cijena_eur_g)}</td><td>{Number(h.nadmasivanje_eur).toFixed(2)} €</td><td>{h.created_by_name ?? '—'}</td>
                    </tr>)}
                    {history.length === 0 ? <tr><td colSpan={5} style={{ padding: 14, color: '#aaa', textAlign: 'center' }}>{t('Nema unosa.', 'No entries.')}</td></tr> : null}
                </tbody>
            </table>
        </div>
    );
}

// ── NA ODOBRENJE (voditelj / posebno) ────────────────────────────────────────
function OdobravanjeTab({ token, locale, state }: { token: string | null; locale: Locale; state: StateResp }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const views = useMemo(() => {
        const v: { key: string; label: string }[] = [];
        if (state.is_admin || state.is_voditelj) v.push({ key: 'pending_voditelj', label: t('Voditelj — unutar raspona', 'Voditelj — within range') });
        if (state.is_admin || state.is_posebno) v.push({ key: 'pending_posebno', label: t('Posebno — iznad maksimalne', 'Special — above maximum') });
        return v;
    }, [state, locale]);
    const [view, setView] = useState(views[0]?.key ?? 'pending_voditelj');
    const [rows, setRows] = useState<RequestRow[]>([]);
    const [busy, setBusy] = useState<number | null>(null);

    const reload = useCallback(async () => { const d = await api<{ rows: RequestRow[] }>(`/otkup50/requests?view=${view}`, token); setRows(d.rows); }, [token, view]);
    useEffect(() => { void reload(); }, [reload]);

    const decide = async (id: number, decision: 'approve' | 'reject') => {
        const note = window.prompt(t('Napomena (opcionalno):', 'Note (optional):')) ?? '';
        setBusy(id);
        try { await mutate(`/otkup50/requests/${id}/decide`, token, 'POST', { decision, note: note || null }); await reload(); }
        catch (e) { alert(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
    };

    return (
        <div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                {views.map((v) => (
                    <button key={v.key} type="button" onClick={() => setView(v.key)} style={{ ...btnGhost, background: view === v.key ? '#8a5a1f' : '#fffdf6', color: view === v.key ? '#fff' : '#2f3337' }}>{v.label}</button>
                ))}
            </div>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead><tr>
                    <th style={TH}>{t('Vrijeme', 'Time')}</th><th style={TH}>{t('Poslovnica', 'Branch')}</th><th style={TH}>{t('Prodavač', 'Seller')}</th>
                    <th style={TH}>{t('Težina', 'Weight')}</th><th style={TH}>{t('Konkurent', 'Competitor')}</th><th style={TH}>{t('Tražena cijena', 'Requested price')}</th>
                    <th style={TH}>{t('Dokaz', 'Evidence')}</th><th style={TH}>{t('Ističe', 'Expires')}</th><th style={TH}></th>
                </tr></thead>
                <tbody>
                    {rows.map((r) => <tr key={r.id} style={{ borderBottom: '1px solid #f0e9d8' }}>
                        <td style={{ padding: 6 }}>{String(r.created_at).slice(0, 16).replace('T', ' ')}</td>
                        <td>{r.branch_name ?? '—'}</td><td>{r.seller_name ?? '—'}</td>
                        <td>{Number(r.weight_g).toFixed(3)} g{r.fineness_permille ? ` (${r.fineness_permille}‰)` : ''}</td>
                        <td>{r.competitor_name ?? '—'} · {fmtEur(r.competitor_price_eur_g)}</td>
                        <td><b>{fmtEur(r.requested_price_eur_g)}</b><div style={{ fontSize: 11, color: '#888' }}>{t('max', 'max')} {fmtEur(r.maksimalna_cijena_eur_g)}</div></td>
                        <td style={{ maxWidth: 160 }}>{r.evidence_reference ?? '—'}</td>
                        <td>{String(r.expires_at).slice(0, 16).replace('T', ' ')}</td>
                        <td style={{ display: 'flex', gap: 6 }}>
                            <button disabled={busy === r.id} onClick={() => void decide(r.id, 'approve')} style={{ ...btn, background: '#1f7d4d', padding: '5px 10px' }}>✓</button>
                            <button disabled={busy === r.id} onClick={() => void decide(r.id, 'reject')} style={{ ...btn, background: '#b3261e', padding: '5px 10px' }}>✕</button>
                        </td>
                    </tr>)}
                    {rows.length === 0 ? <tr><td colSpan={9} style={{ padding: 14, color: '#1f7a36', textAlign: 'center' }}>{t('Nema zahtjeva na čekanju.', 'No pending requests.')}</td></tr> : null}
                </tbody>
            </table>
        </div>
    );
}

// ── DNEVNIK (kategorijski manager / admin) ───────────────────────────────────
function DnevnikTab({ token, locale }: { token: string | null; locale: Locale }) {
    const [rows, setRows] = useState<RequestRow[]>([]);
    useEffect(() => { void api<{ rows: RequestRow[] }>('/otkup50/requests?view=company', token).then((d) => setRows(d.rows)); }, [token]);

    return <RequestsTable rows={rows} locale={locale} showBranch showSeller />;
}

// ── POSTAVKE (admin/superadmin) — dodjela uloga i regija ────────────────────
const ROLE_OPTS: { value: string; hr: string; en: string }[] = [
    { value: 'none', hr: '(nema)', en: '(none)' },
    { value: 'user', hr: 'Prodavač', en: 'Seller' },
    { value: 'voditelj', hr: 'Regionalni voditelj', en: 'Regional lead' },
    { value: 'manager', hr: 'Kategorijski manager', en: 'Category manager' },
    { value: 'posebno', hr: 'Posebno (eskalacija)', en: 'Special (escalation)' },
];

function PostavkeTab({ token, locale }: { token: string | null; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<SettingsResp | null>(null);
    const [filter, setFilter] = useState('');
    const [busy, setBusy] = useState<number | null>(null);
    const [error, setError] = useState('');
    const [regionEditor, setRegionEditor] = useState<number | null>(null);
    const [regionDraft, setRegionDraft] = useState<string[]>([]);

    const reload = useCallback(async () => { setData(await api<SettingsResp>('/otkup50/settings', token)); }, [token]);
    useEffect(() => { void reload(); }, [reload]);

    const regionsByUser = useMemo(() => {
        const m: Record<number, string[]> = {};
        (data?.voditelj_regions ?? []).forEach((r) => { (m[r.voditelj_user_id] ??= []).push(r.region); });
        return m;
    }, [data]);

    const setRole = async (userId: number, role: string) => {
        setError(''); setBusy(userId);
        try { await mutate('/otkup50/settings/role', token, 'POST', { user_id: userId, role }); await reload(); }
        catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
    };

    const openRegions = (userId: number) => { setRegionEditor(userId); setRegionDraft(regionsByUser[userId] ?? []); };
    const toggleRegion = (region: string) => { setRegionDraft((d) => (d.includes(region) ? d.filter((r) => r !== region) : [...d, region])); };
    const saveRegions = async () => {
        if (regionEditor == null) return;
        setBusy(regionEditor);
        try {
            await mutate('/otkup50/settings/voditelj-regions', token, 'PUT', { voditelj_user_id: regionEditor, regions: regionDraft });
            setRegionEditor(null);
            await reload();
        } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
    };

    if (!data) return <div style={{ color: '#888' }}>…</div>;
    const needle = filter.trim().toLowerCase();
    const filtered = data.users.filter((u) => !needle || u.name.toLowerCase().includes(needle) || u.email.toLowerCase().includes(needle));

    return (
        <div>
            <div style={card}>
                <strong>{t('Dodjela uloga — m.26 Otkup 50g+', 'Role assignment — m.26 Buyback 50g+')}</strong>
                <p style={{ color: '#888', fontSize: 12 }}>
                    {t(
                        'Prodavač ne treba posebnu ulogu (zadano stanje "nema"). Ista osoba smije istovremeno biti manager i posebno — V1 ograničenje je jedan redak po korisniku, pa za dvije uloge iste osobe (npr. Slaven/Tabak) koristite dva različita korisnička zapisa ako treba oboje odjednom.',
                        'Seller does not need a special role (default "none"). The same person may be both manager and posebno — V1 limitation is one row per user, so for two roles on the same person (e.g. Slaven/Tabak) use two different user records if both are needed at once.',
                    )}
                </p>
                {error ? <div style={{ background: '#fdecec', color: '#b3261e', padding: 8, borderRadius: 8, marginBottom: 8 }}>{error}</div> : null}
                <input placeholder={t('Pretraga (ime/email)', 'Search (name/email)')} value={filter} onChange={(e) => setFilter(e.target.value)} style={{ ...input, width: 260 }} />
            </div>

            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead><tr>
                    <th style={TH}>{t('Korisnik', 'User')}</th><th style={TH}>{t('Email', 'Email')}</th>
                    <th style={TH}>{t('Uloga m.26', 'm.26 Role')}</th><th style={TH}>{t('Regije (voditelj)', 'Regions (voditelj)')}</th>
                </tr></thead>
                <tbody>
                    {filtered.map((u) => {
                        const role = data.roles[String(u.id)] ?? 'none';
                        const myRegions = regionsByUser[u.id] ?? [];
                        return (
                            <tr key={u.id} style={{ borderBottom: '1px solid #f0e9d8' }}>
                                <td style={{ padding: 6 }}>{u.name}</td>
                                <td style={{ color: '#888' }}>{u.email}</td>
                                <td>
                                    <select disabled={busy === u.id} value={role} onChange={(e) => void setRole(u.id, e.target.value)} style={input}>
                                        {ROLE_OPTS.map((o) => <option key={o.value} value={o.value}>{locale === 'en' ? o.en : o.hr}</option>)}
                                    </select>
                                </td>
                                <td>
                                    {role === 'voditelj' ? (
                                        regionEditor === u.id ? (
                                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', maxWidth: 380 }}>
                                                {data.regions.map((r) => (
                                                    <label key={r} style={{ fontSize: 12, display: 'flex', gap: 3, alignItems: 'center' }}>
                                                        <input type="checkbox" checked={regionDraft.includes(r)} onChange={() => toggleRegion(r)} /> {r}
                                                    </label>
                                                ))}
                                                <button disabled={busy === u.id} onClick={() => void saveRegions()} style={{ ...btn, padding: '4px 8px' }}>{t('Spremi', 'Save')}</button>
                                                <button onClick={() => setRegionEditor(null)} style={{ ...btnGhost, padding: '4px 8px' }}>{t('Odustani', 'Cancel')}</button>
                                            </div>
                                        ) : (
                                            <button onClick={() => openRegions(u.id)} style={{ ...btnGhost, padding: '4px 8px', fontSize: 12 }}>
                                                {myRegions.length ? myRegions.join(', ') : t('(postavi regije)', '(set regions)')}
                                            </button>
                                        )
                                    ) : <span style={{ color: '#ccc' }}>—</span>}
                                </td>
                            </tr>
                        );
                    })}
                    {filtered.length === 0 ? <tr><td colSpan={4} style={{ padding: 14, color: '#aaa', textAlign: 'center' }}>{t('Nema rezultata.', 'No results.')}</td></tr> : null}
                </tbody>
            </table>
        </div>
    );
}

function RequestsTable({ rows, locale, showBranch, showSeller }: { rows: RequestRow[]; locale: Locale; showBranch: boolean; showSeller: boolean }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    return (
        <table style={{ width: '100%', marginTop: 6, fontSize: 13, borderCollapse: 'collapse' }}>
            <thead><tr>
                <th style={TH}>{t('Vrijeme', 'Time')}</th>
                {showBranch ? <th style={TH}>{t('Poslovnica', 'Branch')}</th> : null}
                {showSeller ? <th style={TH}>{t('Prodavač', 'Seller')}</th> : null}
                <th style={TH}>{t('Faza', 'Stage')}</th><th style={TH}>{t('Tražena cijena', 'Requested price')}</th>
                <th style={TH}>{t('Status', 'Status')}</th><th style={TH}>{t('Odluka', 'Decision')}</th>
            </tr></thead>
            <tbody>
                {rows.map((r) => {
                    const meta = STATUS_META[r.status] ?? { hr: r.status, en: r.status, color: '#888' };
                    return (
                        <tr key={r.id} style={{ borderBottom: '1px solid #f0e9d8' }}>
                            <td style={{ padding: 6 }}>{String(r.created_at).slice(0, 16).replace('T', ' ')}</td>
                            {showBranch ? <td>{r.branch_name ?? '—'}</td> : null}
                            {showSeller ? <td>{r.seller_name ?? '—'}</td> : null}
                            <td>{r.approval_stage}</td>
                            <td>{fmtEur(r.requested_price_eur_g)}</td>
                            <td><span style={{ color: meta.color, fontWeight: 700 }}>{locale === 'en' ? meta.en : meta.hr}</span></td>
                            <td>{r.approver_name ? `${r.approver_name}${r.approval_notes ? ` — ${r.approval_notes}` : ''}` : '—'}</td>
                        </tr>
                    );
                })}
                {rows.length === 0 ? <tr><td colSpan={showBranch && showSeller ? 7 : 6} style={{ padding: 14, color: '#aaa', textAlign: 'center' }}>{t('Nema zapisa.', 'No records.')}</td></tr> : null}
            </tbody>
        </table>
    );
}
